/**
 * Uber Tools — the Uber Eats menu utilities, lifted out of Inventory Tools so
 * everything Uber sits under the one UBER EATS nav section (Jason, 2026-09-16).
 * Same card grid as Inventory Tools; each card opens its tool in place.
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
} from '@mui/material';
import {
  AddShoppingCart as AddProductIcon,
  Backup as BackupIcon,
  CompareArrows as ReconcileIcon,
  Sync as SyncIcon,
  AccountTree as MappingsIcon,
} from '@mui/icons-material';
import AddProductToUber from './Tools/AddProductToUber';
import MenuBackupRestore from './Tools/MenuBackupRestore';
import UberMenuReconciliation from './Tools/UberMenuReconciliation';
import SyncAppToUber from './Tools/SyncAppToUber';
import UberMenuMappings from './Tools/UberMenuMappings';

type ActiveTool = 'add-product' | 'menu-backup' | 'menu-reconcile' | 'sync-app-to-uber' | 'menu-mappings' | null;

const tools = [
  {
    id: 'menu-mappings' as const,
    title: 'Uber Menu Mappings',
    description:
      'Decide what goes on Uber Eats and in which aisle: each aisle holds a Lightspeed category and/or a Lightspeed tag. Anything not mapped stays off Uber.',
    icon: <MappingsIcon sx={{ fontSize: 40 }} />,
    color: '#1565c0',
  },
  {
    id: 'sync-app-to-uber' as const,
    title: 'Push Uber Menu',
    description:
      'Build the Uber Eats menu from the mappings, preview it (with why anything was left out), then replace the live menu in one go, with a backup first.',
    icon: <SyncIcon sx={{ fontSize: 40 }} />,
    color: '#2e7d32',
  },
  {
    id: 'add-product' as const,
    title: 'Add Product to Uber',
    description:
      'Search the catalog for a product, transform and preview it, then publish directly to your Uber Eats store menu with automatic backup.',
    icon: <AddProductIcon sx={{ fontSize: 40 }} />,
    color: '#00897b',
  },
  {
    id: 'menu-backup' as const,
    title: 'Menu Backup & Restore',
    description:
      'Create snapshots of your Uber Eats menu before changes, browse backup history, and restore a previous menu version instantly.',
    icon: <BackupIcon sx={{ fontSize: 40 }} />,
    color: '#e65100',
  },
  {
    id: 'menu-reconcile' as const,
    title: 'Menu Reconciliation',
    description:
      'Compare Uber Eats menu against the product catalog. Find orphaned items and missing products, then batch-add them to Uber.',
    icon: <ReconcileIcon sx={{ fontSize: 40 }} />,
    color: '#7b1fa2',
  },
];

const UberToolsPage: React.FC = () => {
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);

  if (activeTool === 'add-product') {
    return <AddProductToUber onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'menu-backup') {
    return <MenuBackupRestore onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'menu-reconcile') {
    return <UberMenuReconciliation onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'menu-mappings') {
    return <UberMenuMappings onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'sync-app-to-uber') {
    return <SyncAppToUber onBack={() => setActiveTool(null)} />;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Uber Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Uber Eats menu utilities
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {tools.map((tool) => (
          <Card
            key={tool.id}
            variant="outlined"
            sx={{
              flex: '1 1 300px',
              maxWidth: 380,
              display: 'flex',
              flexDirection: 'column',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${tool.color}14`,
                  color: tool.color,
                  mb: 2,
                }}
              >
                {tool.icon}
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {tool.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tool.description}
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setActiveTool(tool.id)}
                sx={{ bgcolor: tool.color, '&:hover': { bgcolor: tool.color } }}
              >
                Open
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default UberToolsPage;
